/**
 * Transition Engine — handles take/transitions from Preview to Program
 * Zone-aware: renders items into their assigned zone containers.
 * Multi-zone transitions only affect the target zone, preserving other zones.
 */
import { monitorStore } from '../stores/monitorStore';
import { projectStore } from '../stores/projectStore';
import { zoneStore, DESIGN_W, DESIGN_H } from '../stores/zoneStore';
import { renderTemplate, applyContentBoundsZoom, restartSharedVideo } from './templateRenderer';
import { broadcastProgramState, broadcastProgramClear } from './videoBoardChannel';
import type { TransitionType } from '@graphicsim/shared-types';

let programCanvas: HTMLElement;

// Lazy import to avoid circular dependency — renderProgramWithZones is set after init
let renderProgramWithZonesFn: (() => void) | null = null;

export function initTransitionEngine(canvas: HTMLElement): void {
  programCanvas = canvas;
}

/** Called by program.ts to provide the zone rendering function */
export function setZoneRenderer(fn: () => void): void {
  renderProgramWithZonesFn = fn;
}

/**
 * Execute a take — bring the preview item to program with transition
 */
export function executeTake(): void {
  const { previewItemId, transitionType, transitionDuration } = monitorStore.getState();
  if (!previewItemId) return;

  const item = projectStore.getState().getItem(previewItemId);
  if (!item) return;

  monitorStore.getState().setTransitioning(true);

  if (transitionType === 'cut') {
    // Immediate cut — no animation
    monitorStore.getState().takeToProgram();
    renderProgramZones();
    monitorStore.getState().setTransitioning(false);
  } else {
    // Animated transition — takeToProgram is called inside applyTransition
    applyTransition(previewItemId, transitionType, transitionDuration, false);
  }
}

/**
 * Send a specific item directly to program (used by Play button in Project panel).
 * Always uses fade transition. 2nd click fades the item off program.
 */
export function executeTakeItem(itemId: string): void {
  const item = projectStore.getState().getItem(itemId);
  if (!item) {
    console.warn('[TakeItem] Item not found:', itemId);
    return;
  }

  const FADE_DURATION = 500;

  // If already on program, fade it out
  const { programItemIds } = monitorStore.getState();
  if (programItemIds.includes(itemId)) {
    console.debug('[TakeItem] Already on program — fading out', item.name, itemId);
    fadeOutItem(itemId, FADE_DURATION);
    return;
  }

  console.debug('[TakeItem] Sending to program:', item.name, 'mediaType=', item.mediaType, 'mediaUrl=', item.mediaUrl, itemId);

  // Restart video from beginning when playing
  if (item.mediaType === 'video' && item.mediaUrl) {
    restartSharedVideo(item.mediaUrl);
  }

  // Add to program state first, then animate
  monitorStore.getState().sendItemToProgram(itemId);
  monitorStore.getState().setTransitioning(true);
  // skipTake=true because sendItemToProgram already added the item
  applyTransition(itemId, 'fade', FADE_DURATION, true);
}

/**
 * Fade an item out of its zone on the program canvas, then remove it from program state.
 * Only clears the target zone — other zones are preserved.
 */
function fadeOutItem(itemId: string, duration: number): void {
  const durationMs = `${duration}ms`;
  const item = projectStore.getState().getItem(itemId);
  const largestZoneId = zoneStore.getState().getLargestZoneId();
  const targetZoneId = item?.zoneId ?? largestZoneId;
  const preset = zoneStore.getState().getActivePreset();

  monitorStore.getState().setTransitioning(true);

  if (preset.zones.length === 1) {
    // Full layout — fade out the entire canvas content
    const content = programCanvas.querySelector('.template-viewport, .template-inner');
    if (content) {
      (content as HTMLElement).style.setProperty('--transition-duration', durationMs);
      content.classList.add('tpl-transition-fade-out');
    }
  } else {
    // Multi-zone — fade out only the target zone content
    const zoneContainer = programCanvas.querySelector<HTMLElement>(`.pgm-zone[data-zone-id="${targetZoneId}"]`);
    if (zoneContainer) {
      const content = zoneContainer.querySelector('.template-viewport, .template-inner');
      if (content) {
        (content as HTMLElement).style.setProperty('--transition-duration', durationMs);
        content.classList.add('tpl-transition-fade-out');
      }
    }
  }

  setTimeout(() => {
    monitorStore.getState().removeFromProgram(itemId);

    if (preset.zones.length === 1) {
      // Full layout — full rebuild (or clear if no items left)
      renderProgramZones();
    } else {
      // Multi-zone — just clear the target zone, preserve other zones
      const zoneContainer = programCanvas.querySelector<HTMLElement>(`.pgm-zone[data-zone-id="${targetZoneId}"]`);
      if (zoneContainer) {
        zoneContainer.innerHTML = '';
      }
      // If no more items on program, clean up entirely
      if (monitorStore.getState().programItemIds.length === 0) {
        programCanvas.innerHTML = '';
        programCanvas.classList.remove('program-active');
        broadcastProgramClear();
      } else {
        broadcastProgramState(programCanvas);
      }
    }

    monitorStore.getState().setTransitioning(false);
  }, duration);
}

/**
 * Clear program output
 */
export function clearProgram(): void {
  programCanvas.innerHTML = '';
  programCanvas.classList.remove('program-active');
  monitorStore.getState().clearProgram();
  broadcastProgramClear();
}

/**
 * Render program with zones. Uses the external renderer if available,
 * otherwise falls back to single-item rendering.
 */
function renderProgramZones(): void {
  if (renderProgramWithZonesFn) {
    renderProgramWithZonesFn();
  } else {
    // Fallback: render last program item fullscreen
    const programIds = monitorStore.getState().programItemIds;
    if (programIds.length > 0) {
      programCanvas.classList.add('program-active');
      renderTemplate(programCanvas, programIds[programIds.length - 1]);
    }
  }
}

/**
 * Apply a transition animation for an item entering the program.
 * @param skipTake - if true, skip takeToProgram() (item already added via sendItemToProgram)
 */
function applyTransition(itemId: string, type: TransitionType, duration: number, skipTake: boolean): void {
  const durationMs = `${duration}ms`;

  // Update store state (only for Take button path, not Play button path)
  if (!skipTake) {
    monitorStore.getState().takeToProgram();
  }

  // For zone-based rendering, we do the transition on the specific zone container
  const item = projectStore.getState().getItem(itemId);
  const preset = zoneStore.getState().getActivePreset();
  const largestZoneId = zoneStore.getState().getLargestZoneId();
  const targetZoneId = item?.zoneId ?? largestZoneId;

  // If Full Layout (single zone), use the old full-canvas transition
  if (preset.zones.length === 1) {
    applyFullCanvasTransition(itemId, type, durationMs, duration);
    return;
  }

  // Multi-zone: ensure zone containers exist, then transition within the target zone
  ensureZoneContainers();

  const zoneContainer = programCanvas.querySelector<HTMLElement>(`.pgm-zone[data-zone-id="${targetZoneId}"]`);
  if (!zoneContainer) {
    renderProgramZones();
    monitorStore.getState().setTransitioning(false);
    return;
  }

  // Create a wrapper for the incoming content within the zone
  const wrapper = document.createElement('div');
  wrapper.className = 'program-transition-in';
  wrapper.style.cssText = `position:absolute;inset:0;z-index:1;--transition-duration:${durationMs};`;

  switch (type) {
    case 'dissolve':
    case 'fade':
      wrapper.classList.add('tpl-transition-fade-in');
      break;
    case 'push-left':
      wrapper.classList.add('tpl-transition-push-left');
      break;
    case 'push-right':
      wrapper.classList.add('tpl-transition-push-right');
      break;
  }

  // Fade out old content in this zone
  const oldContent = zoneContainer.querySelector('.template-inner');
  if (oldContent && (type === 'dissolve' || type === 'fade')) {
    (oldContent as HTMLElement).style.cssText += `--transition-duration:${durationMs};`;
    oldContent.classList.add('tpl-transition-fade-out');
  }

  zoneContainer.appendChild(wrapper);
  renderTemplate(wrapper, itemId);

  // After transition: clean up only this zone — other zones stay untouched.
  // Render into an inner wrapper so .template-viewport is never added to the
  // .pgm-zone container (which would break its absolute positioning).
  setTimeout(() => {
    zoneContainer.innerHTML = '';
    const inner = document.createElement('div');
    inner.style.cssText = 'position:absolute;inset:0;';
    zoneContainer.appendChild(inner);
    renderTemplate(inner, itemId);
    if (item?.templateId) {
      applyContentBoundsZoom(inner, item.templateId);
    }
    monitorStore.getState().setTransitioning(false);
    broadcastProgramState(programCanvas);
  }, duration);
}

function applyFullCanvasTransition(itemId: string, type: TransitionType, durationMs: string, duration: number): void {
  const wrapper = document.createElement('div');
  wrapper.className = 'program-transition-in';
  wrapper.style.cssText = `position:absolute;inset:0;--transition-duration:${durationMs};`;

  switch (type) {
    case 'dissolve':
    case 'fade':
      wrapper.classList.add('tpl-transition-fade-in');
      break;
    case 'push-left':
      wrapper.classList.add('tpl-transition-push-left');
      break;
    case 'push-right':
      wrapper.classList.add('tpl-transition-push-right');
      break;
  }

  programCanvas.classList.add('program-active');
  programCanvas.style.position = 'relative';

  const oldContent = programCanvas.querySelector('.template-inner');
  if (oldContent && (type === 'dissolve' || type === 'fade')) {
    (oldContent as HTMLElement).style.cssText += `--transition-duration:${durationMs};`;
    oldContent.classList.add('tpl-transition-fade-out');
  }

  programCanvas.appendChild(wrapper);
  renderTemplate(wrapper, itemId);

  setTimeout(() => {
    renderProgramZones();
    monitorStore.getState().setTransitioning(false);
  }, duration);
}

/** Ensure zone containers exist in program canvas */
function ensureZoneContainers(): void {
  const existing = programCanvas.querySelector('.pgm-zone');
  if (existing) return; // already set up

  programCanvas.classList.add('program-active');
  programCanvas.style.position = 'relative';

  const preset = zoneStore.getState().getActivePreset();
  for (const zone of preset.zones) {
    const container = document.createElement('div');
    container.className = 'pgm-zone';
    container.dataset.zoneId = String(zone.id);
    container.style.left = `${(zone.x / DESIGN_W * 100).toFixed(4)}%`;
    container.style.top = `${(zone.y / DESIGN_H * 100).toFixed(4)}%`;
    container.style.width = `${(zone.w / DESIGN_W * 100).toFixed(4)}%`;
    container.style.height = `${(zone.h / DESIGN_H * 100).toFixed(4)}%`;
    programCanvas.appendChild(container);
  }
}
